import React from 'react';
import { useLocation } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../../hooks';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Site, Study, UpdateRequest } from '../../../models';
import {
  EntityState,
  EntityStatus,
  siteIdSchema,
} from 'adc-sirius-module-common';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';
import { enumToChoiceOptions } from '../../../utilities/helpers';
import { QrCodePopup } from '../../qrCode/QrCodePopup';
import { useMutation } from '@tanstack/react-query';
import { updateSiteMutateOptions } from '../../../queryOptions';

type SiteHeaderProps = {
  site: Site;
  study: Study;
};

type SiteFormValues = Site & UpdateRequest;

/**
 * SiteHeader component displays the header information for a specific site.
 * @param {Site} site - The site object containing details to display.
 * @param {Study} study - The study object associated with the site.
 * @returns {React.JSX.Element}
 */
export default function SiteHeader({ site, study }: SiteHeaderProps) {
  const location = useLocation();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const updateBreadcrumb = (label) => {
    navigateWithBreadcrumbs(location.pathname, label, label, {
      replace: true,
    });
  };

  const mapFormValuesToSite = (values: SiteFormValues): Site => {
    const { newUpdateReason, ...updatedValues } = values;
    const updatedSite = {
      ...site,
      ...updatedValues,
      status: Number(updatedValues.status),
      updateReason: newUpdateReason,
    };

    return updatedSite;
  };

  const mapSiteToFormValues = (site: Site): SiteFormValues => ({
    ...site,
    newUpdateReason: '',
  });

  const formConfig: DynamicFormConfig<SiteFormValues> = {
    fields: [
      {
        name: 'name',
        label: i18n.t('Page_Site.Form.name'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'description',
        label: i18n.t('Page_Site.Form.description'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'status',
        label: i18n.t('Page_Site.Form.status'),
        type: DynamicFormFieldType.CHOICE,
        isEditable: true,
        choiceOptions: enumToChoiceOptions(EntityStatus, 'Status', {
          sortBy: 'name',
        }),
      },
      {
        name: 'region',
        label: i18n.t('Page_Site.Form.region'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'type',
        label: i18n.t('Page_Site.Form.type'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Site.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Site.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Site.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Site.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Site.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Site.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string().required(i18n.t('Form.genericRequired')),
      name: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .matches(
          siteIdSchema.matcher,
          i18n.t('Form.allowedSpecialCharacters', {
            characters: siteIdSchema.allowedSpecialCharacters.join(' '),
          }),
        )
        .test(
          'len',
          () =>
            i18n.t('Form.genericMaxLength', { count: siteIdSchema.maxLength }),
          (val) =>
            siteIdSchema.maxLength && val
              ? val.length < siteIdSchema.maxLength
              : true,
        ),
      description: Yup.string(),
      studyKey: Yup.string().required(i18n.t('Form.genericRequired')),
      region: Yup.string(),
      status: Yup.string().required(i18n.t('Form.genericRequired')),
      createdBy: Yup.string().required(i18n.t('Form.genericRequired')),
      createdOn: Yup.string().required(i18n.t('Form.genericRequired')),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      state: Yup.mixed()
        .oneOf(Object.values(EntityState))
        .required(i18n.t('Form.genericRequired')),
      type: Yup.string(),
    },
    initialValues: mapSiteToFormValues(site),
  };

  const updateSite = useMutation(updateSiteMutateOptions());

  const handleSubmit = async (values: Record<string, any>) => {
    const siteValues = mapFormValuesToSite(values as SiteFormValues);
    const updatedSite = await updateSite.mutateAsync({
      siteKey: site.key,
      updatedSiteValues: siteValues,
    });

    updateBreadcrumb(updatedSite.name);

    const updatedFormValues = mapSiteToFormValues(updatedSite);

    return updatedFormValues;
  };

  return (
    <VStack flexGrow={1} gap={'$4'}>
      <Card>
        <DynamicForm
          id="site-details"
          title={i18n.t('Page_Site.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
        >
          <QrCodePopup
            studyIdentifier={study.name}
            siteIdentifier={site.name}
            studyKey={study.key}
            siteKey={site.key}
          />
        </DynamicForm>
      </Card>
    </VStack>
  );
}
